"use client"

import { Component, type ReactNode } from "react"
import { captureClientException } from "@/shared/lib/monitoring/capture-client"

interface ErrorBoundaryProps {
  children: ReactNode
  /** Reporting scope passed to PostHog (e.g. "create-wizard"). */
  scope: string
  /**
   * Fallback UI. Receives `reset`, which clears the error so the subtree
   * re-mounts. Defaults to a full reload, which also recovers from
   * `ChunkLoadError` (a stale deploy serving a missing JS chunk) by refetching.
   */
  fallback: (props: { reset: () => void; error: Error }) => ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Client error boundary. Catches render-phase errors in its subtree, reports
 * them to PostHog, and renders a recoverable fallback — so a failed render
 * (hydration mismatch, chunk-load failure) surfaces a retry affordance instead
 * of silently leaving the page un-interactive.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    captureClientException(error, {
      scope: this.props.scope,
      extra: { boundary: this.props.scope, componentStack: info.componentStack },
    })
  }

  reset = () => {
    this.setState({ error: null })
  }

  render() {
    if (this.state.error) {
      return this.props.fallback({ reset: this.reset, error: this.state.error })
    }
    return this.props.children
  }
}
